import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { mockUser, enrollmentHistory, paymentHistory, favoriteItems, consultationHistory, mypageTabs } from "@/mocks/mypage";
import { motion, AnimatePresence } from "motion/react";

const headerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const } },
};

const sidebarVariants = {
  hidden: { opacity: 0, x: -24 },
  visible: { opacity: 1, x: 0, transition: { delay: 0.1, duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const } },
};

const contentVariants = {
  hidden: { opacity: 0, x: 24 },
  visible: { opacity: 1, x: 0, transition: { delay: 0.15, duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const } },
};

const listContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.07 } },
};

const listItem = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

export default function MyPage() {
  const { section } = useParams();
  const navigate = useNavigate();
  const activeTab = section || "courses";

  return (
    <div className="min-h-screen bg-[#FAFAF8] font-['Noto_Sans_KR']">
      <Navbar />
      <main className="pt-24 pb-16 px-4 md:px-6">
        <div className="max-w-[1120px] mx-auto">
          {/* Page Header */}
          <motion.div variants={headerVariants} initial="hidden" animate="visible" className="mb-8">
            <div className="flex items-center gap-2 mb-2">
              <i className="ri-user-settings-line text-[#C4956A]"></i>
              <span className="text-xs font-medium text-[#C4956A]">마이페이지</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#1C1917]">마이페이지</h1>
          </motion.div>

          <div className="flex flex-col lg:flex-row gap-6">
            {/* Sidebar - Profile Card */}
            <motion.div variants={sidebarVariants} initial="hidden" animate="visible" className="lg:w-72 shrink-0">
              <div className="bg-white rounded-2xl border border-[#E8E5E0] p-6 sticky top-28">
                <div className="flex flex-col items-center text-center mb-5">
                  <div className="w-20 h-20 rounded-full overflow-hidden mb-4 border-2 border-[#E8E5E0]">
                    <img loading="lazy" decoding="async" src={mockUser.avatar} alt={mockUser.name} className="w-full h-full object-cover" />
                  </div>
                  <h3 className="text-lg font-bold text-[#1C1917]">{mockUser.name}</h3>
                  <p className="text-sm text-[#8C8780]">{mockUser.email}</p>
                  <span className="inline-block mt-2 px-3 py-0.5 text-xs font-medium bg-[#C4956A]/10 text-[#C4956A] rounded-full">
                    {mockUser.grade} 회원
                  </span>
                </div>

                <nav className="space-y-1">
                  {mypageTabs.map((tab) => (
                    <motion.button
                      key={tab.key}
                      whileHover={{ x: 4 }}
                      onClick={() => navigate(`/mypage/${tab.key}`)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-all text-left ${
                        activeTab === tab.key
                          ? "bg-[#1C1917] text-white font-medium"
                          : "text-[#5C5650] hover:bg-[#FAFAF8] hover:text-[#C4956A]"
                      }`}
                    >
                      <i className={`${tab.icon} text-base`}></i>
                      {tab.label}
                    </motion.button>
                  ))}
                </nav>

                <div className="mt-5 pt-5 border-t border-[#F0EDE8]">
                  <Link
                    to="/login"
                    className="flex items-center gap-2 px-3 py-2.5 text-sm text-[#8C8780] hover:text-red-500 rounded-lg hover:bg-red-50 transition-all"
                  >
                    <i className="ri-logout-box-line text-base"></i>
                    로그아웃
                  </Link>
                </div>
              </div>
            </motion.div>

            {/* Main Content */}
            <motion.div variants={contentVariants} initial="hidden" animate="visible" className="flex-1 min-w-0">
              <AnimatePresence mode="wait">
                {/* Courses Tab */}
                {activeTab === "courses" && (
                  <motion.div
                    key="courses"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -16 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white rounded-2xl border border-[#E8E5E0] p-6 md:p-8"
                  >
                    <h2 className="text-lg font-bold text-[#1C1917] mb-1">수강내역</h2>
                    <p className="text-sm text-[#8C8780] mb-6">총 <strong className="text-[#1C1917]">{enrollmentHistory.length}</strong>개의 과정을 수강 중입니다</p>

                    <motion.div variants={listContainer} initial="hidden" animate="visible" className="space-y-4">
                      {enrollmentHistory.map((enrollment) => (
                        <motion.div key={enrollment.id} variants={listItem} className="flex flex-col sm:flex-row gap-4 p-5 border border-[#E8E5E0] rounded-xl hover:border-[#C4956A]/30 transition-all">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                enrollment.type === "무료" ? "bg-emerald-50 text-emerald-600" :
                                enrollment.type === "오프라인" ? "bg-violet-50 text-violet-600" :
                                "bg-[#C4956A]/10 text-[#C4956A]"
                              }`}>{enrollment.type}</span>
                              {enrollment.progress === 100 && (
                                <span className="px-2 py-0.5 text-xs font-medium bg-emerald-50 text-emerald-600 rounded-full">수료</span>
                              )}
                            </div>
                            <h4 className="text-sm font-semibold text-[#1C1917] mb-1">{enrollment.courseTitle}</h4>
                            <p className="text-xs text-[#8C8780]">
                              신청일 {enrollment.enrolledAt}
                              {enrollment.completedAt && <span> · 수료일 {enrollment.completedAt}</span>}
                            </p>
                            <div className="mt-3 flex items-center gap-3">
                              <div className="flex-1 h-2 bg-[#F0EDE8] rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all"
                                  style={{
                                    width: `${enrollment.progress}%`,
                                    backgroundColor: enrollment.progress === 100 ? "#10B981" : "#C4956A",
                                  }}
                                ></div>
                              </div>
                              <span className="text-xs font-medium text-[#5C5650] w-10 text-right">{enrollment.progress}%</span>
                            </div>
                          </div>
                          <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-center gap-2 shrink-0">
                            {enrollment.price > 0 && (
                              <span className="text-sm font-bold text-[#1C1917]">{enrollment.price.toLocaleString()}원</span>
                            )}
                            <Link
                              to={`/courses/${enrollment.id.replace("e", "")}`}
                              className="px-4 py-2 text-xs font-medium border border-[#E8E5E0] text-[#5C5650] rounded-full hover:border-[#C4956A] hover:text-[#C4956A] transition-all whitespace-nowrap"
                            >
                              {enrollment.progress === 100 ? "다시보기" : "이어서 수강"}
                            </Link>
                          </div>
                        </motion.div>
                      ))}
                    </motion.div>
                  </motion.div>
                )}

                {/* Payments Tab */}
                {activeTab === "payments" && (
                  <motion.div
                    key="payments"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -16 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white rounded-2xl border border-[#E8E5E0] p-6 md:p-8"
                  >
                    <h2 className="text-lg font-bold text-[#1C1917] mb-1">결제내역</h2>
                    <p className="text-sm text-[#8C8780] mb-6">
                      총 결제 금액 <strong className="text-[#1C1917]">{paymentHistory.reduce((sum, p) => sum + p.amount, 0).toLocaleString()}원</strong>
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-[#E8E5E0] text-left">
                            <th className="pb-3 text-xs font-medium text-[#8C8780]">결제일</th>
                            <th className="pb-3 text-xs font-medium text-[#8C8780]">유형</th>
                            <th className="pb-3 text-xs font-medium text-[#8C8780]">항목</th>
                            <th className="pb-3 text-xs font-medium text-[#8C8780] text-right">금액</th>
                            <th className="pb-3 text-xs font-medium text-[#8C8780] text-right">상태</th>
                          </tr>
                        </thead>
                        <motion.tbody variants={listContainer} initial="hidden" animate="visible">
                          {paymentHistory.map((payment) => (
                            <motion.tr key={payment.id} variants={listItem} className="border-b border-[#F0EDE8]">
                              <td className="py-3 text-[#5C5650] whitespace-nowrap">{payment.date}</td>
                              <td className="py-3">
                                <span className="px-2 py-0.5 text-xs bg-[#FAFAF8] text-[#5C5650] rounded-full">
                                  {payment.type}
                                </span>
                              </td>
                              <td className="py-3 text-[#1C1917] font-medium">{payment.item}</td>
                              <td className="py-3 text-[#1C1917] font-semibold text-right whitespace-nowrap">{payment.amount.toLocaleString()}원</td>
                              <td className="py-3 text-right">
                                <span className="px-2 py-0.5 text-xs font-medium bg-emerald-50 text-emerald-600 rounded-full">
                                  {payment.status}
                                </span>
                              </td>
                            </motion.tr>
                          ))}
                        </motion.tbody>
                      </table>
                    </div>
                  </motion.div>
                )}

                {/* Favorites Tab */}
                {activeTab === "favorites" && (
                  <motion.div
                    key="favorites"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -16 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white rounded-2xl border border-[#E8E5E0] p-6 md:p-8"
                  >
                    <h2 className="text-lg font-bold text-[#1C1917] mb-1">관심물건</h2>
                    <p className="text-sm text-[#8C8780] mb-6">총 <strong className="text-[#1C1917]">{favoriteItems.length}</strong>개의 관심 물건</p>
                    <motion.div variants={listContainer} initial="hidden" animate="visible" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {favoriteItems.map((item) => (
                        <motion.div key={item.id} variants={listItem} whileHover={{ y: -4 }}>
                          <Link
                            to={`/auctions/${item.id}`}
                            className="group bg-[#FAFAF8] rounded-xl border border-[#E8E5E0] overflow-hidden hover:border-[#C4956A]/30 transition-all block"
                          >
                            <div className="relative h-40 overflow-hidden">
                              <img loading="lazy" decoding="async"
                                src={item.imageUrl}
                                alt={item.location}
                                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                              />
                              <div className="absolute top-2 left-2">
                                <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${item.grade === "A" ? "bg-emerald-500 text-white" : "bg-amber-500 text-white"}`}>
                                  {item.grade}등급
                                </span>
                              </div>
                              <button
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                className="absolute top-2 right-2 w-7 h-7 bg-white/90 rounded-full flex items-center justify-center hover:bg-red-50 transition-colors"
                                title="관심 해제"
                              >
                                <i className="ri-heart-fill text-sm text-red-400"></i>
                              </button>
                            </div>
                            <div className="p-3.5">
                              <h4 className="text-sm font-semibold text-[#1C1917] mb-2 line-clamp-1">{item.location}</h4>
                              <div className="space-y-1 text-xs">
                                <div className="flex justify-between">
                                  <span className="text-[#8C8780]">최저가</span>
                                  <span className="text-[#C4956A] font-bold">{item.minimumPrice.toLocaleString()}원</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-[#8C8780]">사건번호</span>
                                  <span className="text-[#5C5650]">{item.caseNumber}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-[#8C8780]">입찰일</span>
                                  <span className="text-[#5C5650]">{item.auctionDate}</span>
                                </div>
                              </div>
                            </div>
                          </Link>
                        </motion.div>
                      ))}
                    </motion.div>
                  </motion.div>
                )}

                {/* Consultations Tab */}
                {activeTab === "consultations" && (
                  <motion.div
                    key="consultations"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -16 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white rounded-2xl border border-[#E8E5E0] p-6 md:p-8"
                  >
                    <h2 className="text-lg font-bold text-[#1C1917] mb-1">상담내역</h2>
                    <p className="text-sm text-[#8C8780] mb-6">총 <strong className="text-[#1C1917]">{consultationHistory.length}</strong>건의 상담</p>
                    <motion.div variants={listContainer} initial="hidden" animate="visible" className="space-y-4">
                      {consultationHistory.map((consult) => (
                        <motion.div key={consult.id} variants={listItem} className="p-5 border border-[#E8E5E0] rounded-xl">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-[#C4956A]/10 flex items-center justify-center shrink-0">
                                <i className="ri-customer-service-2-line text-[#C4956A]"></i>
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold text-[#1C1917]">{consult.type}</h4>
                                <p className="text-xs text-[#8C8780]">{consult.date} · {consult.time}</p>
                              </div>
                            </div>
                            <span className={`self-start px-2.5 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                              consult.status === "완료" ? "bg-emerald-50 text-emerald-600" :
                              consult.status === "예정" ? "bg-amber-50 text-amber-600" :
                              "bg-gray-50 text-gray-600"
                            }`}>
                              {consult.status}
                            </span>
                          </div>
                          <div className="bg-[#FAFAF8] rounded-lg p-3">
                            <p className="text-xs text-[#8C8780] leading-relaxed">{consult.summary}</p>
                          </div>
                          {consult.status === "완료" && (
                            <button className="mt-3 text-xs text-[#C4956A] font-medium hover:text-[#B08050] transition-colors">
                              상담 내용 자세히 보기
                            </button>
                          )}
                        </motion.div>
                      ))}
                    </motion.div>
                    <div className="mt-6 text-center">
                      <Link
                        to="/consultation"
                        className="inline-block px-6 py-2.5 text-sm font-medium bg-[#1C1917] text-white rounded-full hover:bg-[#C4956A] transition-all whitespace-nowrap"
                      >
                        <i className="ri-add-line mr-1"></i>
                        새 상담 신청
                      </Link>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}